import { ResultOf } from "gql.tada";
import { cloneElement, ComponentProps, forwardRef, useMemo } from "react";
// eslint-disable-next-line no-restricted-imports
import { Link as RRLink } from "react-router";
import { LinkFragment } from "@queries/fragments/LinkFragment";
import { useArticleModalState } from "@hooks/useArticleModalState";
import { useGlobalContext } from "@utils/GlobalContext";

type LinkProps = { noLocalePrefix?: boolean } & (
  | (ComponentProps<"a"> & { href: string; link?: never })
  | (ComponentProps<"a"> & {
      link: ResultOf<typeof LinkFragment>;
      href?: never;
    })
);

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ href: hrefProp, link, noLocalePrefix = false, ...props }, ref) => {
    const { locale } = useGlobalContext();
    const { setIsArticleModalOpen } = useArticleModalState();
    // Slug encodes the full path:
    //   starts with '/' → direct navigation link (e.g. "/targeting", "/ad-formats")
    //   no leading '/' → article modal (e.g. "viewing-moods", "my-article")
    // gql.tada infers slug as {} — cast to string | null for type safety
    const slug = link?.slug as string | null | undefined;
    const isArticleModal = !!slug && !slug.startsWith("/");
    const href = useMemo(() => {
      const localePrefix = noLocalePrefix
        ? ""
        : `/${locale.toLocaleLowerCase()}`;
      if (link) {
        return link.isExternal ? link.url : localePrefix + (slug ?? "#");
      }
      const internalHref = hrefProp?.startsWith("/");
      return (
        (internalHref ? localePrefix : "") +
        (hrefProp === "/homepage" ? "/" : hrefProp ?? "#")
      );
    }, [hrefProp, slug, noLocalePrefix, locale]) as string;

    const isInternal = useMemo(() => {
      const url = new URL(
        href,
        `${window.location.protocol}//${window.location.host}`
      );
      return url.host === window.location.host;
    }, [href]);

    if (isInternal) {
      const linkElement = <RRLink {...props} to={href} ref={ref} />;

      if (isArticleModal) {
        return cloneElement(linkElement, {
          onClick: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
            props.onClick?.(e);
            if (e.isDefaultPrevented()) return;
            setIsArticleModalOpen(slug ?? "");
            e.preventDefault();
            e.currentTarget.blur();
          },
        });
      } else return linkElement;
    } else {
      return (
        // eslint-disable-next-line react/forbid-elements
        <a
          {...props}
          href={href}
          ref={ref}
          target="_blank"
          rel="noopener noreferrer"
        />
      );
    }
  }
);
